import * as React from 'react';

type UseKeyboardOpenOptions = {
  enabled?: boolean;
};

const isEditableElement = (element: Element | null): boolean => {
  if (!element) {
    return false;
  }

  if (!(element instanceof HTMLElement)) {
    return false;
  }

  if (element.isContentEditable) {
    return true;
  }

  const tagName = element.tagName;

  return tagName === 'INPUT' || tagName === 'TEXTAREA';
};

const getKeyboardOpen = (): boolean => {
  const { documentElement } = document;

  if (documentElement.hasAttribute('data-native-webview')) {
    return documentElement.hasAttribute('data-keyboard-open');
  }

  return isEditableElement(document.activeElement);
};

export const useKeyboardOpen = (options: UseKeyboardOpenOptions = {}): boolean => {
  const { enabled = true } = options;
  const [isOpen, setIsOpen] = React.useState(false);

  React.useEffect(() => {
    if (!enabled) {
      setIsOpen(false);

      return;
    }

    const viewport = window.visualViewport;
    const update = () => setIsOpen(getKeyboardOpen());
    const observer = new MutationObserver(update);

    observer.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ['data-keyboard-open', 'data-native-webview'],
    });

    window.addEventListener('resize', update, { passive: true });
    window.addEventListener('orientationchange', update, { passive: true });

    if (viewport) {
      viewport.addEventListener('resize', update, { passive: true });
      viewport.addEventListener('scroll', update, { passive: true });
    }

    document.addEventListener('focusin', update);
    document.addEventListener('focusout', update);

    update();

    return () => {
      observer.disconnect();
      window.removeEventListener('resize', update);
      window.removeEventListener('orientationchange', update);

      if (viewport) {
        viewport.removeEventListener('resize', update);
        viewport.removeEventListener('scroll', update);
      }

      document.removeEventListener('focusin', update);
      document.removeEventListener('focusout', update);
    };
  }, [enabled]);

  return isOpen;
};

export default useKeyboardOpen;
